<template>
  <div data-sb-regression="sb-columns-drawer">
    <sb-drawer
      v-model:active="activeModel"
      version="next"
      close-variation="x"
      side="right"
      :container-class="$style.columnsFilter"
    >
      <template #drawer-header>
        <slot
          v-if="$slots.header"
          name="header"
        />
        <h3 v-else>
          {{ $t('sbFilterBar.columnOptions.header') }}
        </h3>
      </template>
      <div data-sb-automation="column-selection-drawer">
        <slot name="content-header" />
        <sb-draggable
          :items="columns"
          item-key="field"
          handle=".handle"
          @drag-end="handlerDrag"
        >
          <template #sb-drag-item="{ element }">
            <div
              :class="$style.draggableColumn"
              data-sb-automation="column-selection-row"
            >
              <sb-checkbox
                data-sb-automation="column-selection-row-checkbox-container"
                version="next"
                :name="getKey(element)"
                :class="$style.checkbox"
                :checked="element.enabled"
                @update:checked="() => handlerToggle(element)"
              >
                <div :class="$style.colName">
                  {{ getLabel(element) }}
                </div>
              </sb-checkbox>
              <span
                class="handle"
                data-sb-automation="column-row-handle"
              >
                <sb-svg-icon
                  :class="$style.dragHandleIcon"
                  :svg="DragHandle"
                />
              </span>
            </div>
          </template>
        </sb-draggable>
      </div>
      <template #drawer-footer>
        <sb-button
          version="next"
          variation="minimal"
          data-sb-automation="column-selection-reset-columns"
          @click="handlerResetColumns"
        >
          {{ $t('sbFilterBar.columnOptions.resetColumns') }}
        </sb-button>
        <sb-button
          version="next"
          data-sb-automation="column-selection-update-columns"
          @click="handlerSave"
        >
          {{ $t('sbFilterBar.columnOptions.updateColumns') }}
        </sb-button>
      </template>
    </sb-drawer>
  </div>
</template>

<script setup>
import { ref, defineProps, defineModel, defineEmits, onMounted, nextTick } from 'vue';

import DragHandle from '@public/media/icons/drag-handle.svg';
import SbDrawer from '@src/components/Drawer/SbDrawer.vue';
import SbDraggable from '@src/components/Draggable/SbDraggable.vue';
import SbButton from '@src/components/Button/SbButton.vue';
import SbCheckbox from '@src/components/Checkbox/SbCheckbox.vue';
import SbSvgIcon from '@src/components/Icons/components/SbSvgIcon.vue';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';

const activeModel = defineModel('active');
const appliedColumns = defineModel('appliedColumns', {
  type: Array,
});
const columns = ref([]);

const props = defineProps({
  columnDefinitions: {
    type: Array,
  },
});

function getKey(column) {
  return get(column, 'row.field');
}
function getLabel(column) {
  return get(column, 'header.name');
}

// Methods
function getColumns(withReset) {
  const selection = withReset ? props.columnDefinitions : appliedColumns.value;
  const orderMap = selection.reduce((prev, curr, index) => {
    const object = prev;
    object[getKey(curr)] = index;
    return object;
  }, {});
  const result = cloneDeep(props.columnDefinitions).map((column) => {
    const key = getKey(column);
    return {
      ...column,
      enabled: withReset || orderMap[key] >= 0,
    };
  }).sort((a, b) => {
    if (!a.enabled && !b.enabled) return 0;
    if (!a.enabled) return 1;
    if (!b.enabled) return -1;
    return orderMap[getKey(a)] - orderMap[getKey(b)];
  });
  return result;
}

function handlerToggle(column) {
  const i = columns.value.findIndex((c) => getKey(c) === getKey(column));
  if (i > -1) {
    columns.value[i].enabled = !columns.value[i].enabled;
  }
}

function handlerDrag(columnsUpdated) {
  columns.value = columnsUpdated.data;
}

function handlerSave() {
  appliedColumns.value = columns.value.filter((column) => column.enabled);
  activeModel.value = false;
  nextTick(() => {
    columns.value = getColumns();
  });
}

function handlerResetColumns() {
  columns.value = getColumns(true);
}

onMounted(() => {
  columns.value = getColumns();
});
</script>

<style lang="scss" module>
@use '@src/components/Filter/next/styles/columnsDrawer.module.scss';
</style>
